import type {
  V1AutomationInputSpec,
  Commonv1Value,
  V1ValueType,
} from '@/generated/hooks/grimoire/models';
import type { InputComponentType } from '../types';
import {
  unwrapType,
  getInputComponentType,
  getListElementType,
  isBooleanType,
  isFileListType,
  isLeafComponentType,
  isValueEmpty,
} from './typeDetection';
import { toDisplayValue } from './valueConversion';

/**
 * Result of processing an input specification
 */
export interface ProcessedInputSpec {
  /** The unwrapped base type after removing optional wrappers */
  baseType: V1ValueType;
  /** Whether the input is optional */
  isOptional: boolean;
  /** Whether the input is a multiple file upload */
  isMultipleFiles: boolean;
  /** The type of input component to render */
  componentType: InputComponentType;
  /**
   * For `componentType === 'list'`, the unwrapped element type. Undefined for
   * every other component type.
   */
  elementType: V1ValueType | undefined;
  /**
   * For `componentType === 'list'`, the component type each element renders
   * with. Always a leaf component type.
   */
  elementComponentType: InputComponentType | undefined;
  /** The current value for this input */
  currentValue: Commonv1Value | undefined;
  /** Validation error message for this input, if any */
  error: string | undefined;
  /** Whether the input is required */
  required: boolean;
  /** The display-formatted value */
  displayValue: string | boolean;
  /**
   * For `componentType === 'list'`, the current elements as display values.
   * Empty for every other component type.
   */
  listItems: string[];
}

/**
 * Resolves which component renders a given base type, resolving lists against
 * their element type.
 *
 * A list is not a single kind of input — it depends entirely on `T`:
 * - `List[file]` uses the multi-select dropzone, because file pickers already
 *   accept several files at once and one picker per row would be worse.
 * - `List[leaf]` uses the generic element-row editor.
 * - anything else (list of dictionaries, tables, or a nested list) falls back
 *   to the JSON textarea, since only one level of nesting is supported.
 *
 * @param baseType - The unwrapped base type
 * @returns The component type plus, for lists, the resolved element info
 */
function resolveComponentType(baseType: V1ValueType): {
  componentType: InputComponentType;
  elementType: V1ValueType | undefined;
  elementComponentType: InputComponentType | undefined;
} {
  if (isFileListType(baseType)) {
    return {
      componentType: 'file',
      elementType: undefined,
      elementComponentType: undefined,
    };
  }

  const elementType = getListElementType(baseType);
  if (elementType) {
    const elementComponentType = getInputComponentType(elementType);
    if (isLeafComponentType(elementComponentType)) {
      return { componentType: 'list', elementType, elementComponentType };
    }
    // TODO: support List[dictionary] / List[table] / nested lists once the
    // structured editors for those types exist. Until then the whole list
    // stays on the JSON textarea.
    return {
      componentType: 'complex',
      elementType: undefined,
      elementComponentType: undefined,
    };
  }

  return {
    componentType: getInputComponentType(baseType),
    elementType: undefined,
    elementComponentType: undefined,
  };
}

/**
 * Processes an input specification to extract all necessary information for rendering
 *
 * Takes an input spec and extracts the base type, determines the appropriate component
 * to render, gets the current value and error state, and formats the display value.
 *
 * @param spec - The automation input specification
 * @param values - Current values for all inputs, keyed by field name
 * @param errors - Optional validation errors for inputs, keyed by field name
 * @returns Processed input spec with all rendering information
 */
export function processInputSpec(
  spec: V1AutomationInputSpec,
  values: Record<string, Commonv1Value>,
  errors?: Record<string, string>,
): ProcessedInputSpec {
  // Unwrap type to get base type and metadata
  const { baseType, isOptional } = unwrapType(spec.type);

  const { componentType, elementType, elementComponentType } =
    resolveComponentType(baseType);

  // Get current value and error
  const currentValue = values[spec.name];
  const error = errors?.[spec.name];

  // Determine if required
  const required = !isOptional;

  // Convert to display value
  const displayValue = toDisplayValue(currentValue);

  // Elements are edited as display values, mirroring every other input here.
  const listItems =
    componentType === 'list'
      ? (currentValue?.list?.items ?? []).map(item =>
          String(toDisplayValue(item)),
        )
      : [];

  return {
    baseType,
    isOptional,
    isMultipleFiles: componentType === 'file' && isFileListType(baseType),
    componentType,
    elementType,
    elementComponentType,
    currentValue,
    error,
    required,
    displayValue,
    listItems,
  };
}

/**
 * Builds the input record to send to the runtime, filling in every declared
 * input spec rather than only the ones the user touched.
 *
 * The runtime distinguishes "not provided" from "explicitly empty", so an
 * optional input the user left alone must be sent as `Value::None` rather than
 * omitted — otherwise the automation sees a missing argument instead of a
 * deliberate absence.
 *
 * Untouched booleans are the one exception: a checkbox always has a concrete
 * state, so an unset one is a real `false`, not an absent value.
 *
 * @param inputSpecs - The automation's declared input specifications
 * @param values - Current values from the inputs sheet, keyed by field name
 * @returns One entry per input spec, ready to send
 */
export function buildRunInputs(
  inputSpecs: V1AutomationInputSpec[] | undefined,
  values: Record<string, Commonv1Value>,
): Record<string, Commonv1Value> {
  const processed: Record<string, Commonv1Value> = {};

  for (const spec of inputSpecs ?? []) {
    if (!spec.name) continue;

    const value = values[spec.name];

    if (value && !isValueEmpty(value)) {
      processed[spec.name] = value;
    } else if (isBooleanType(spec.type)) {
      processed[spec.name] = { bool_value: false };
    } else {
      processed[spec.name] = { null_value: 'NULL_VALUE' };
    }
  }

  return processed;
}
